import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Lock } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { usePlatformContext } from "../context/PlatformContext";

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { platformInfo } = usePlatformContext();

  useEffect(() => {
    // Check if already authenticated
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        const user = response.data;
        if (user.role === 'admin') {
          navigate('/admin', { replace: true });
        } else if (user.role === 'reseller') {
          navigate('/reseller', { replace: true });
        } else if (user.role === 'distributor') {
          navigate('/distributor', { replace: true });
        } else {
          // Default fallback (though customers shouldn't be here)
          navigate('/', { replace: true });
        }
      } catch (error) {
        // Not authenticated, stay on login page
      }
    };
    checkAuth();
  }, [navigate]);

  const handleGoogleLogin = () => {
    // However, App.js AuthCallback handles the actual routing relative to role.
    const redirectUrl = window.location.origin + '/login';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const user = response.data;

      // Store session token
      if (user.session_token) {
        localStorage.setItem('session_token', user.session_token);
      }

      toast.success("Login successful!");

      if (user.role === 'customer') {
        toast.error("Access Denied: Customers cannot access the partner portal.");
        // Clear session if any
        localStorage.removeItem('session_token');
        return;
      }

      console.log("Login Response User:", user);

      if (user.role === 'customer') {
        toast.error("Access Denied: Customers cannot access the partner portal.");
        localStorage.removeItem('session_token');
        return;
      }

      if (user.role === 'admin') {
        navigate('/admin', { replace: true });
      } else if (user.role === 'distributor') {
        navigate('/distributor', { replace: true });
      } else if (user.role === 'reseller') {
        console.log("Redirecting Reseller to Dashboard");
        navigate('/reseller', { replace: true });
      } else {
        // Fallback for unexpected roles, but block customer
        toast.error("Access Denied: Invalid role.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-indigo-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="absolute top-10 left-10 w-72 h-72 bg-amber-200/40 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-indigo-200/30 rounded-full blur-3xl"></div>

      {/* Wider than the usual form card so the two enlarged logos sit side by side
          without crowding (TR-22). */}
      <div className="w-full max-w-lg relative">
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/50 p-8">
          <div className="text-center mb-8">
            {/* Co-branded: Infinity Touch beside the admin-uploaded eDisplayRoom mark.
                Each brand takes half the card width and the pair is centred as a unit
                (TR-22), so neither side crowds the other whatever the logos measure.
                The IT wordmark is ~4.9:1, so it stays capped shorter than the ED logo —
                equal heights would let it dominate the row. */}
            <div className="flex items-center justify-center gap-2 sm:gap-4 mb-5">
              <div className="flex-1 basis-1/2 flex justify-end">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/it-website/img/logo.png`}
                  alt="Infinity Touch"
                  className="h-8 sm:h-12 w-auto max-w-full object-contain drop-shadow-md"
                />
              </div>
              {platformInfo?.platform_logo_url ? (
                <>
                  <span className="w-px h-8 sm:h-12 bg-slate-200 shrink-0" />
                  {/* Logo + name, mirroring the storefront header (components/layout/Header.jsx).
                      The name is DB-driven (platform_name), never hardcoded. Sized down on
                      mobile (below sm) so both brand halves still fit on one line instead
                      of the eDisplayroom mark pushing past the card edge. */}
                  <div className="flex-1 basis-1/2 flex items-center justify-start gap-1.5 sm:gap-2 min-w-0">
                    <img
                      src={platformInfo.platform_logo_url}
                      alt={platformInfo.platform_name || 'eDisplayroom'}
                      className="h-8 sm:h-16 w-auto max-w-[60px] sm:max-w-[120px] object-contain drop-shadow-md shrink-0"
                    />
                    <div className="flex flex-col min-w-0 text-left">
                      <span className="text-xs sm:text-base font-bold text-slate-900 leading-tight truncate">
                        {platformInfo.platform_name || 'eDisplayroom'}
                      </span>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
            <h1 className="text-2xl font-bold text-slate-900">
              Infinity Suite
            </h1>
            <p className="text-xs text-slate-500">{platformInfo?.platform_tagline || 'by Infinity Funeral Supply'}</p>
            <p className="text-slate-600 mt-4">
              Sign in to access your dashboard
            </p>
          </div>

          {/* Email Login Form */}
          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-10 h-12 bg-white border-slate-200 focus:border-amber-500 focus:ring-amber-500"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm text-amber-600 hover:text-amber-700 hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 h-12 bg-white border-slate-200 focus:border-amber-500 focus:ring-amber-500"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 rounded-xl h-12 text-white font-medium shadow-lg shadow-amber-200"
              data-testid="email-login-btn"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>





          <p className="text-sm text-slate-500 mt-6 text-center">
            Don't have a store yet?{" "}
            <a href="/" className="text-amber-600 hover:text-amber-700 font-medium">
              Request one here
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
